import { DownOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetOnHoldWorkerReportListParams } from '@nxsys/store/slices/report/types';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { formatFullName } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Dropdown } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOnHoldWorkerColumns } from './onHoldWorkerColumns';

const OnHoldWorkerReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch<AppDispatch>();
  const { isBureauEmployer, user, payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const { onHoldWorkerReportList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = onHoldWorkerReportList;

  const [selectedRows, setSelectedRows] = useState<EmployeeRecordType[]>([]);

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const handleViewEmployee = (record: EmployeeRecordType) => {
    navigate(`/employees-subcontractors/${record.id}`, {
      query: {
        from: '/reports/on-hold-worker',
      },
    });
  };

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.workerOnHold'] || 'Worker on Hold'}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        {
          key: 'export',
          label: messages['common.export'],
          onClick: handleExportSelected,
        },
        {
          key: 'exportAll',
          label: messages['common.exportAll'],
          onClick: handleExportAll,
        },
      ],
    };
    return (
      <Dropdown menu={menuProps}>
        <AppButton
          type="primary"
          name={messages['common.export']}
          rightIcon={<DownOutlined />}
        />
      </Dropdown>
    );
  };

  const handleFetchData = useCallback(
    (params: GetOnHoldWorkerReportListParams) => {
      const newParams = {
        ...params,
        companyRefs: isBureauEmployer ? user?.companyUserIds || [] : undefined,
      };
      dispatch(reportActions.getOnHoldWorkerReportList(newParams));
    },
    [dispatch, isBureauEmployer, user?.companyUserIds],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: EmployeeRecordType[]) => {
    const dataExport = data.map((item) => {
      return {
        status: item.status || '_',
        id: item.id || '_',
        name: formatFullName(item) || '_',
        emailId: item.email || '_',
        mobileNumber: item.mobile || '_',
        employer: item.companyPayment?.companyName || '_',
        registrationStatus: item.registrationStatus || '_',
        payFrequency: item.payFrequency || '_',
        niNumber: item.NINumber || '_',
        niCategory: item.setting?.niCategory || '_',
        taxCode: item.setting?.taxCode || '_',
        lastPaidDate: item.latestPayroll?.paymentDate
          ? moment(item.latestPayroll.paymentDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
        lastPaidPeriod:
          item.latestPayroll?.taxPeriod && item.latestPayroll?.taxYear
            ? `${item.latestPayroll.taxPeriod}/${item.latestPayroll.taxYear}`
            : '_',
      };
    });

    const headers = [
      'Status',
      'Employee ID',
      'Name',
      'Email ID',
      'Mobile Number',
      'Employer',
      'Registration Status',
      'Pay Frequency',
      payrollCountry === IRELAND ? 'PPS Number' : 'NI Number',
      'NI Category',
      'Tax Code',
      'Last Paid Date',
      'Last Paid Period',
    ];

    const csvExporter = new ExportToCsv({
      filename: `Worker-on-Hold-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = async () => {
    if (!pagination.totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    const params: GetOnHoldWorkerReportListParams = {
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
      companyRefs: isBureauEmployer ? user?.companyUserIds || [] : undefined,
    };

    try {
      const action = await dispatch(
        reportActions.getOnHoldWorkerReportList(params),
      );
      if (
        reportActions.getOnHoldWorkerReportList.fulfilled.match(action) &&
        action.payload.data.length > 0
      ) {
        handleExport(action.payload.data);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const isFiltering = !!queryParams?.search;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2400, y: 700 }}
            columns={getOnHoldWorkerColumns({
              messages,
              onView: handleViewEmployee,
              payrollCountry,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
    </AppsContainer>
  );
};

export default OnHoldWorkerReport;
